/**
 * Wraps up the knockout.validation localization into our framework.
 */
define('extensions/knockout.validation.extender',[
    'moment',
    'lib/knockout',
    'lib/lodash4',
    'util/networkUtils',
    'services/loggingService',
    // no references
    'lib/knockout.validation',
    'extensions/i18n/knockout.i18n'
], function (moment, ko, _, networkUtils, loggingService) {

    'use strict';

    /**
     * Validates that a string is an IP of various types based on the input params.
     * @param {string} val - The string to verify
     * @param {object} params
     *  isEmptyValid - '' will be treated as valid
     *  validateCIDR - undefined will allow CIDR and non-CIDR, otherwise enforces based on this value
     *  isIPv6 - Defaults to IPv4, will validate IPv6 is true
     * @returns {boolean}
     */
    var isIp = function (val, params) {

        var retVal = true;
        var ip = ko.utils.unwrapObservable(val);
        var isEmptyValid = ko.utils.unwrapObservable(params.isEmptyValid);

        if (isEmptyValid && (!ip || ip.length <= 0)) {
            retVal = true;
        }
        else {
            var isIpv4Address = networkUtils.isIpv4(ip);
            var isIPv4CIDR = networkUtils.isIpv4Cidr(ip);
            var isIpv6Address = networkUtils.isIpv6(ip);
            var isIPv6CIDR = networkUtils.isIpv6Cidr(ip);

            var validateCIDR = ko.utils.unwrapObservable(params.validateCIDR);

            //validate against IPv4 by default
            if (!_.hasValue(validateCIDR)) {
                retVal = isIpv4Address || isIPv4CIDR;
            }
            else {
                retVal = validateCIDR ? isIPv4CIDR : isIpv4Address;
            }

            //check if params is an object
            if (typeof params === 'object') {
                //obtain type of IP being validated
                var isIPv6 = ko.utils.unwrapObservable(params.isIPv6);

                if (isIPv6) {

                    if (!_.hasValue(validateCIDR)) {
                        retVal = isIpv6Address || isIPv6CIDR;
                    }
                    else {
                        retVal = validateCIDR ? isIPv6CIDR : isIpv6Address;
                    }
                }
            }
        }
        return retVal;
    };

    //
    // Add custom KO validation rules here
    //

    // CUSTOM VALIDATOR: Array must exist and contain n or more items.
    ko.validation.rules.minItems = {
        validator: function (array, quantity) {

            var self = this,
                arrayValue = ko.unwrap(array);

            if (quantity == 1) {
                self.message = 'knockout.validation.minItems.singluar';
            }
            // empty data does NOT fail validation
            if (_.isNull(arrayValue) || _.isUndefined(arrayValue)) {
                return true;
            }
            return _.isArray(arrayValue) && arrayValue.length >= quantity;
        }
    };

    // CUSTOM VALIDATOR: Array must exist and contain less than n items.
    ko.validation.rules.maxItems = {
        validator: function (array, quantity) {

            var self = this,
                arrayValue = ko.unwrap(array);

            if (quantity == 1) {
                self.message = 'knockout.validation.maxItems.singluar';
            }
            // empty data does NOT fail validation
            if (_.isNull(arrayValue) || _.isUndefined(arrayValue)) {
                return true;
            }
            return _.isArray(arrayValue) && arrayValue.length <= quantity;
        }
    };

    // CUSTOM VALIDATOR: Numeric value must be an integer.
    ko.validation.rules.isInteger = {
        validator: function (observable) {
            var value = ko.unwrap(observable);
            return Number(value) % 1 === 0;
        }
    };

    /**
     * Validates number of decimal places or if a value is a decimal number
     *
     * Example 1:
     * // validates that a value has 4 decimal places
     * // 3.2222, -50.1111, .2222 are all valid values
     *
     * self.myDecimal.extend({
     *     decimal: 4
     * })
     *
     * Example 2:
     * // validates that the number is a decimal value - but could also be an integer.
     * // 3.5, 4, -3.14 all work
     *
     * self.myDecimal.extend({
     *     decimal: true
     * })
     *
     * @type {{validator: validator}}
     */
    ko.validation.rules.decimal = {
        validator: function (value, decimals  ) {
            var self = this,
                regexp;

            if (_.isNumber( decimals )){
                regexp = new RegExp(  "^-?\\s*[\\d]*\\.[\\d]{"+ decimals +"}$", "ig" );
                self.message = 'knockout.validation.decimal.length';
            } else {
                regexp = new RegExp( "^-?\\s*[\\d]*\\.?[\\d]*$", "ig" );
                self.message = 'knockout.validation.decimal';
            }

            return regexp.test(value);
        }
    };

    // CUSTOM VALIDATOR: Value must match one or more patterns. Numeric values will cast to a string.
    ko.validation.rules.multiPattern = {
        validator: function (observable, patterns) {
            var value = ko.unwrap(observable);
            var valueAsString = String(value);

            // This is not a REQUIRED FIELD validator... so there actually has to be a value for it to apply.
            if (valueAsString.length > 0) {
                return _.some(patterns, function (pattern) {
                    return valueAsString.match(new RegExp(pattern));
                });
            }
            return true;
        }
    };

    ko.validation.rules.anyConditional = {
        validator: function (observable, conditionals) {
            var value = ko.unwrap(observable);

            // null, undefined, and empty strings will be returned
            // true.  Use a seperate required validator if you want
            // empty to be in error.
            if (!_.isEmpty(value)) {
                return _.some(conditionals, function (conditional) {
                    return conditional(value);
                });
            }
            return true;
        }
    };

    ko.validation.rules.conditionalRequired = {
        validator: function (val, condition) {
            var required = (typeof condition === 'function') ? condition() : condition;

            if (required) {
                return !(val === undefined || val === null || val.length === 0);
            }
            else {
                return true;
            }
        }
    };

    ko.validation.rules.isLocalTime = {
        validator: function (observable) {
            return moment(observable, 'HH:mm').isValid() ||
                moment(observable, 'HH:mm:ss').isValid() ||
                moment(observable, 'HH:mm:ss.SSS').isValid();
        }
    };

    ko.validation.rules.isLocalTimeBefore = {
        validator: function (observable, condition) {
            var DATE = '01/01/1970 ';
            var FORMAT = 'MM/DD/YYYY HH:mm:ss.SSS';

            return moment(DATE + observable, FORMAT).isBefore(moment(DATE + condition, FORMAT));
        }
    };

    ko.validation.rules.dateTimeBeforeOrEqual = {
        validator: function (val, otherVal) {
            if (!(val && otherVal)) {
                return false;
            }
            return !moment(val).isAfter(otherVal);
        }
    };

    ko.validation.rules.isDurationGreater = {
        validator: function (duration, threshold) {
            return duration <= threshold;
        }
    };

    ko.validation.rules.arrayIncludesValue = {
        validator: function (value, arrayValues) {
            return _.includes(arrayValues, value);
        }
    };

    ko.validation.rules.arrayIncludesEveryValue = {
        validator: function (value, arrayItems) {
            return value.every(function (item) {
                return _.includes(arrayItems, item);
            });
        }
    };


    ko.validation.rules.isDurationLessThanOptional = {
        validator: function (duration, threshold) {
            if (threshold === 0){
                return true;
            }
            return duration > threshold;
        }
    };

    ko.validation.rules.isDurationNotEqual = {
        validator: function (val, otherVal) {
            var self = this;
            if (otherVal === 0) {
                self.message = 'knockout.validation.invalidDuration';
            } else {
                self.message = 'knockout.validation.durationNotEqual';
            }

            return val !== otherVal;
        }
    };

    ko.validation.rules.isDurationLess = {
        validator: function (duration, threshold) {
            return duration >= threshold;
        }
    };

    ko.validation.rules.isScoreGreater = {
        validator: function(score, threshold) {
            return score <= threshold;
        }
    };

    ko.validation.rules.isScoreLess = {
        validator: function(score, threshold) {
            return score >= threshold;
        }
    };

    // CSV List Validation
    ko.validation.rules.csvListValidation = {
        validator: function (content, options) {
            var targetObsv = options.obsv;
            var exp = options.regEx;
            var listArray = targetObsv();
            var regEx = new RegExp(exp);
            var errorDetails = targetObsv.errorDetails = [];
            var foundInvalid = false;

            _.each(listArray, function (listItem, index) {
                var item = (listItem + "").trim();
                if (!regEx.test(item)) {
                    var entry = {};
                    entry.index = index;
                    foundInvalid = true;
                    // See csvErrorDisplay for details on how these errors are used
                    entry.errorDetails = ko.i18n.renderString('knockout.validation.csvListValidation.regex.failure');
                    errorDetails[index] = entry;
                }
            });
            targetObsv.errorDetails = errorDetails;
            return !foundInvalid;
        }
    };

    ko.validation.rules.isIPv4 = {
        validator: function (val, params) {
            return isIp(val, _.defaults(params, {isIPv6: false, validateCIDR: false}));
        }
    };

    ko.validation.rules.isIPv4Cidr = {
        validator: function (val, params) {
            return isIp(val, _.defaults(params, {isIPv6: false, validateCIDR: true}));
        }
    };

    ko.validation.rules.isIPv6 = {
        validator: function (val, params) {
            return isIp(val, _.defaults(params, {isIPv6: true, validateCIDR: false}));
        }
    };
    ko.validation.rules.isIPv6Cidr = {
        validator: function (val, params) {
            return isIp(val, _.defaults(params, {isIPv6: true, validateCIDR: true}));
        }
    };

    ko.validation.rules.isIPv4OrCidr = {
        validator: function (val, params) {
            return isIp(val, _.defaults(params, {isIPv6: false, validateCIDR: undefined}));
        }
    };

    ko.validation.rules.isIP = {
        validator: isIp
    };

    ko.validation.rules.isSubnetMask = {
        validator: function (val, params) {
            var retVal = false;
            var subnet = ko.utils.unwrapObservable(val);
            var isEmptyValid = ko.utils.unwrapObservable(params.isEmptyValid);
            var allowDottedZero = ko.utils.unwrapObservable(params.allowDottedZero);

            retVal = (isEmptyValid && (!subnet || subnet.length <= 0)) ? true : networkUtils.isIpv4SubnetMask(subnet, allowDottedZero);

            return retVal;
        }
    };

    ko.validation.rules.isNetworkIPv4 = {
        validator: function (val, params) {

            var self = this;
            var retVal = false;
            var ipAddress = ko.utils.unwrapObservable(val);
            var subnetMask = ko.utils.unwrapObservable(params.subnetMask);

            retVal = networkUtils.isNetworkIPv4(ipAddress,subnetMask);

            params.isMessageLocalized = false;//init to false

            if (!retVal.isValid) {
                params.isMessageLocalized = true;
                var localizedMessage = ko.i18n.renderString('knockout.validation.isNetworkIPv4.validSubnet',{validNetworkIP:retVal.validNetworkIP});
                self.message = localizedMessage;
            }
            return retVal.isValid;
        }
    };

    ko.validation.rules.isNetworkIPv6 = {
        validator: function (val, params) {
            var self = this;
            var retVal = false;
            var ipAddress = ko.utils.unwrapObservable(val);
            retVal = networkUtils.isNetworkIPv6(ipAddress);
            params.isMessageLocalized = false;//init to false
            if (!retVal.isValid) {
                params.isMessageLocalized = true;
                var localizedMessage = retVal.validNetworkIP?ko.i18n.renderString('knockout.validation.isNetworkIPv6.validSubnet',{validNetworkIP:retVal.validNetworkIP}): ko.i18n.renderString('knockout.validation.isNetworkIPv6.invalid');
                self.message = localizedMessage;
            }
            return retVal.isValid;
        }
    };

    ko.validation.rules.isSubnetMask = {
        validator: function (val, params) {
            var retVal = false;
            var subnet = ko.unwrap(val);
            var isEmptyValid = ko.unwrap(params.isEmptyValid);
            var allowDottedZero = ko.unwrap(params.allowDottedZero);

            retVal = (isEmptyValid && (!subnet || subnet.length <= 0)) ? true : networkUtils.isIpv4SubnetMask(subnet, allowDottedZero);

            return retVal;
        }
    };

    ko.validation.rules.isHostname = {
        validator: function (val) {
            var hostname = ko.unwrap(val);
            return networkUtils.isHostname(hostname);
        }
    };

    ko.validation.rules.isUri = {
        validator: function (val) {
            var uri = ko.unwrap(val);
            return networkUtils.isUri(uri);
        }
    };

    ko.validation.rules.isTimeIntervalValid = {
        validator: function (val, params) {
            var intervalObj = val.split(params.timeSeparator);
            var startTime = intervalObj[0].trim();
            var endTime   = intervalObj[1].trim();
            var tempDateFormat     = 'MM-DD-YYYY';
            var tempDateTimeFormat = 'MM-DD-YYYY h:mm A';

            var tempDateString     = moment().format(tempDateFormat);

            var startTimeMoment    = moment(tempDateString + " " + startTime.trim(),tempDateTimeFormat, 'en', true);
            var endTimeMoment      = moment(tempDateString + " " + endTime.trim(),tempDateTimeFormat, 'en', true);

            if (!startTimeMoment.isValid() || !endTimeMoment.isValid()){
                return false;
            }

            return startTimeMoment.isSame(endTimeMoment) ||
                startTimeMoment.isBefore(endTimeMoment) ||
                endTimeMoment.format('h:mm A') === "12:00 AM";
        }
    };

    // Coupled with the ServiceResponseValidator.js for async validation
    ko.validation.rules.responseValidator = {
        async: true,
        validator: function (val, params, callback) {
            params.setValidationCallbacks(function () {
                callback(true);
            }, function (getMessageDetails) {
                var messageDetails = getMessageDetails(val);
                callback({
                    isValid: false,
                    message: messageDetails.message
                });
            });
        }
    };

    // Validates that a string's length is between 1 and maxLength unless its question is marked NA
    ko.validation.rules.maxLengthUnlessNA = {
        validator: function(val, options){
            if(!options.naSelected()){
                return val.length <= options.maxLength();
            }

            return true;
        },
        message: function(options){
            return ko.i18n.renderString("knockout.validation.maxLength", {maxResponseCharacters: options.maxLength()});
        }
    };

    ko.validation.registerExtenders();

    // CUSTOM VALIDATOR: A custom validator that can access a specific closure scope.
    ko.validation.rules.closureBasedStrategy = {
        validator: function (value, strategy) {
            return strategy.call(this, value);
        }
    };

    // Set the Knockout-Validation text keys.
    var localizedDefaults = {
        conditionalRequired: 'knockout.validation.required',
        required: 'knockout.validation.required',
        min: 'knockout.validation.min',
        max: 'knockout.validation.max',
        minLength: 'knockout.validation.minLength',
        maxLength: 'knockout.validation.maxLength',
        pattern: 'knockout.validation.pattern',
        multiPattern: 'knockout.validation.pattern',
        step: 'knockout.validation.step',
        email: 'knockout.validation.email',
        date: 'knockout.validation.date',
        dateTimeBeforeOrEqual: 'knockout.validation.dateTimeBeforeOrEqual',
        dateISO: 'knockout.validation.dateISO',
        number: 'knockout.validation.number',
        digit: 'knockout.validation.digit',
        phoneUS: 'knockout.validation.phoneUS',
        equal: 'knockout.validation.equal',
        notEqual: 'knockout.validation.notEqual',
        unique: 'knockout.validation.unique',
        minItems: 'knockout.validation.minItems',
        maxItems: 'knockout.validation.maxItems',
        csvListValidation: 'knockout.validation.csvListValidation',
        isLocalTime: 'knockout.validation.isLocalTime',
        isLocalTimeBefore: 'knockout.validation.isLocalTimeBefore',
        closureBasedStrategy: 'knockout.validation.pattern',
        isInteger: 'knockout.validation.integer',
        isDurationGreater: 'knockout.validation.invalidDurationRange',
        isDurationLess: 'knockout.validation.invalidDurationRange',
        isDurationLessThanOptional: 'knockout.validation.invalidOptionalDuration',
        isScoreGreater: 'knockout.validation.invalidScoreRange',
        isScoreLess: 'knockout.validation.invalidScoreRange',
        isSubnetMask: 'knockout.validation.ipv4.subnetMask.invalid',
        isIp: 'knockout.validation.ipv4.invalid',
        isIPv4OrCidr: 'knockout.validation.ipv4OrCidr.invalid',
        isIPv4: 'knockout.validation.ipv4.invalid',
        isIPv6: 'knockout.validation.ipv6.invalid',
        isIPv6Cidr: 'knockout.validation.ipv6Cidr.invalid',
        isIPv4Cidr: 'knockout.validation.ipv4Cidr.invalid',
        isHostname: 'knockout.validation.isHostname.invalid',
        isUri: 'knockout.validation.isUri.invalid',
        isTimeIntervalValid: 'knockout.validation.isTimeIntervalValid.invalid',
        anyConditional: 'knockout.validation.anyConditional.invalid',
        isNetworkIPv4: 'knockout.validation.isNetworkIPv4.invalid',
        isNetworkIPv6: 'knockout.validation.isNetworkIPv6.invalid',
        maxLengthUnlessNA: 'knockout.validation.maxLengthUnlessNA.invalid'
    };

    ko.validation.localize(localizedDefaults);

    // Override the Knockout-Validation message formatter to use our Globalize lib for standard messages.
    ko.validation.formatMessage = function (message, params) {

        if (params && params.isMessageLocalized){
            return message;
        }

        var renderedString = ko.i18n.renderString(message, {
            "value": params
        });
        if (!renderedString || renderedString.length === 0) {
            loggingService.debug('could not find validation message in localization file: ' + message);
        }
        return renderedString;
    };

    // Prevent the library from automatically injecting validation messages into the DOM.
    ko.validation.init({insertMessages: false});


});

